

'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { 
    getAuth, 
    onAuthStateChanged, 
    User, 
    signInWithPopup, 
    signOut,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    getRedirectResult,
    signInWithRedirect,
    GoogleAuthProvider
} from 'firebase/auth';
import { app } from '@/lib/firebase-app';
import { googleProvider, calendarGoogleProvider } from '@/lib/client-db';
import { getV3Db } from '@/lib/v3/db';
import { useRouter, usePathname } from 'next/navigation';
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp, increment } from 'firebase/firestore';
import { v3UserDoc } from '@/lib/v3/collections';
import { AppUser } from '@/lib/types';
import { getSuperAdmins } from '@/app/admin-actions';
import { subscribeSetting } from '@/lib/v3/reads';
import { setSetting } from '@/lib/v3/writes';
import { DASHBOARD_PATH } from '@/lib/app-routes';
import { subscriptionStatusGrantsAccess } from '@/lib/subscription-access';

const auth = getAuth(app);

const DEFAULT_V3_ONBOARDING = {
  welcomed: false,
  homeAddressSet: false,
  firstGameAdded: false,
  firstImportDone: false,
  voiceIntroSeen: false,
};

/** When false while masquerading as admin, subscription gates treat access as paid (test subscribe UX when true). */
const MASQUERADE_ENFORCE_SUBSCRIPTION_KEY = 'masqueradeEnforceSubscription';

/** Dev-only structured subscription logs (email only in development). */
function logSubscriptionDebug(payload: Record<string, unknown>) {
  if (process.env.NODE_ENV !== "development") return;
  console.info("[OfficialsPay][subscription]", payload);
}

export type UiMode = 'baseball' | 'multi-sport';

/** Normalize legacy Firestore uiMode values to current UiMode */
function normalizeUiMode(value: string | undefined): UiMode {
  if (value === 'multi-sport' || value === 'baseball') return value as UiMode;
  if (value === 'pro') return 'multi-sport';
  if (value === 'basic') return 'baseball';
  return 'baseball';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  getAccessToken: () => Promise<string | null>;
  /**
   * Firebase ID token for the **signed-in account** (true admin), even while masquerading.
   * Prefer this over `user.getIdToken()` — masquerade mode uses a plain `user` object without that method.
   */
  getFirebaseIdToken: () => Promise<string>;
  /** Trigger incremental Google OAuth consent for the calendar scope. */
  requestCalendarAccess: () => Promise<string | null>;
  masqueradeAs: (targetUser: AppUser) => void;
  stopMasquerading: () => void;
  isMasquerading: boolean;
  trueAdminUser: User | null;
  masqueradeUser: AppUser | null;
  isSuperSuperAdmin: boolean;
  isAdmin: boolean;
  adminList: string[];
  fetchAdminList: () => Promise<void>;
  uiMode: UiMode;
  setUiMode: (mode: UiMode) => Promise<void>;
  /** True when subscription status grants full access (active or trialing), after masquerade admin bypass */
  hasActiveSubscription: boolean;
  /** True while the subscription status is still loading from Firestore, after masquerade admin bypass */
  subscriptionLoading: boolean;
  /**
   * When masquerading, if false (default), paid gates are bypassed for admins.
   * If true, the target user's real subscription applies (session-persisted).
   */
  enforceMasqueradeSubscription: boolean;
  setEnforceMasqueradeSubscription: (enforce: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [trueAdminUser, setTrueAdminUser] = useState<User | null>(null);
  const [masqueradeUser, setMasqueradeUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminList, setAdminList] = useState<string[]>([]);
  const [uiMode, setUiModeState] = useState<UiMode>('baseball');
  const [isUiModeInitialized, setIsUiModeInitialized] = useState(false);
  const [subscriptionActiveRaw, setSubscriptionActiveRaw] = useState(false);
  const [subscriptionLoadingRaw, setSubscriptionLoadingRaw] = useState(true);
  const [enforceMasqueradeSubscription, setEnforceMasqueradeSubscriptionState] =
    useState(false);
  /** Prevents duplicate POST /api/stripe/sync-subscription per signed-in uid in this session. */
  const stripeSubscriptionSyncUidRef = useRef<string | null>(null);
  // Refs for values used inside onAuthStateChanged so the listener is stable
  // and does not tear down / resubscribe on state or navigation changes.
  const trueAdminUserRef = useRef<User | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const routerRef = useRef(router);
  routerRef.current = router;

  const isSuperSuperAdmin = trueAdminUser?.email === 'aculich@gmail.com';
  const isAdmin = isSuperSuperAdmin || adminList.includes(trueAdminUser?.email || '');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setEnforceMasqueradeSubscriptionState(
      sessionStorage.getItem(MASQUERADE_ENFORCE_SUBSCRIPTION_KEY) === 'true',
    );
  }, []);

  const setEnforceMasqueradeSubscription = useCallback((enforce: boolean) => {
    setEnforceMasqueradeSubscriptionState(enforce);
    if (typeof window !== 'undefined') {
      if (enforce) {
        sessionStorage.setItem(MASQUERADE_ENFORCE_SUBSCRIPTION_KEY, 'true');
      } else {
        sessionStorage.removeItem(MASQUERADE_ENFORCE_SUBSCRIPTION_KEY);
      }
    }
  }, []);

  const masqueradeSubscriptionBypass =
    !!masqueradeUser && isAdmin && !enforceMasqueradeSubscription;

  const hasActiveSubscription = useMemo(
    () =>
      masqueradeSubscriptionBypass ? true : subscriptionActiveRaw,
    [masqueradeSubscriptionBypass, subscriptionActiveRaw],
  );

  const subscriptionLoading = useMemo(
    () =>
      masqueradeSubscriptionBypass ? false : subscriptionLoadingRaw,
    [masqueradeSubscriptionBypass, subscriptionLoadingRaw],
  );

  const getFirebaseIdToken = useCallback(async (): Promise<string> => {
    const cu = auth.currentUser;
    if (!cu) {
      throw new Error("Not signed in");
    }
    return cu.getIdToken();
  }, []);

  const fetchAdminList = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
        const idToken = await getFirebaseIdToken();
        const admins = await getSuperAdmins(idToken);
        setAdminList(admins);
    } catch (e) {
        // Safely extract error message before logging to avoid serialization issues
        let errorMessage = 'Unknown error';
        try {
          if (e instanceof Error) {
            errorMessage = e.message || e.toString();
          } else if (typeof e === 'string') {
            errorMessage = e;
          } else if (e && typeof e === 'object' && 'message' in e) {
            errorMessage = String((e as any).message);
          } else {
            errorMessage = String(e);
          }
        } catch (extractError) {
          errorMessage = 'Error occurred but could not extract message';
        }
        
        // Log only the safe error message, not the entire error object
        console.error("Failed to fetch admin list:", errorMessage);
    }
  }, [user, getFirebaseIdToken]);

  useEffect(() => {
    if (user) {
      void fetchAdminList();
    }
    
    // Set up a listener for real-time updates if needed
    const unsub = onSnapshot(
        doc(getV3Db(), 'admin', 'superadmins'),
        (doc) => {
            if (doc.exists()) {
                setAdminList(doc.data().emails || []);
            }
        },
        () => {
            // Non-admin users lack read access to the admin collection;
            // the server action fetchAdminList handles this case instead.
        }
    );

    return () => unsub();
  }, [fetchAdminList]);
  
  useEffect(() => {
    if (user && !isUiModeInitialized) {
      const unsubscribe = subscribeSetting(user.uid, 'debug', (data) => {
        if (data?.uiMode) {
          setUiModeState(normalizeUiMode(data.uiMode as string));
        }
        setIsUiModeInitialized(true);
      });
      return () => unsubscribe();
    }
  }, [user, isUiModeInitialized]);

  useEffect(() => {
    if (!user) {
      setSubscriptionActiveRaw(false);
      setSubscriptionLoadingRaw(false);
      return;
    }
    // Super-super-admin bypass: avoid onboarding loop when Stripe webhook hasn't run (e.g. local dev)
    if (user.email === 'aculich@gmail.com') {
      setSubscriptionActiveRaw(true);
      setSubscriptionLoadingRaw(false);
      logSubscriptionDebug({
        phase: "super_admin_bypass",
        uid: user.uid,
        email: user.email ?? null,
        hasActiveSubscription: true,
        subscriptionLoading: false,
        firestoreStatus: null,
        source: "bypass",
      });
      return;
    }
    setSubscriptionLoadingRaw(true);
    logSubscriptionDebug({
      phase: "subscription_listener_start",
      uid: user.uid,
      email: user.email ?? null,
      subscriptionLoading: true,
    });
    const unsubscribe = subscribeSetting(user.uid, 'subscription', (data, err) => {
      if (err) {
        console.error('[AuthProvider] subscription settings snapshot error:', err);
      }
      const active = subscriptionStatusGrantsAccess(
        typeof data?.status === "string" ? data.status : undefined,
      );
      setSubscriptionActiveRaw(active);
      setSubscriptionLoadingRaw(false);
      logSubscriptionDebug({
        phase: "firestore_snapshot",
        uid: user.uid,
        email: user.email ?? null,
        source: "firestore",
        firestoreStatus: data?.status ?? null,
        firestorePlan: data?.plan ?? null,
        hasActiveSubscription: active,
        subscriptionLoading: false,
        snapshotError: err ? String(err.message ?? err) : null,
      });
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) {
      stripeSubscriptionSyncUidRef.current = null;
      return;
    }
    if (masqueradeUser) {
      stripeSubscriptionSyncUidRef.current = null;
      return;
    }
    if (stripeSubscriptionSyncUidRef.current === user.uid) {
      return;
    }
    const uid = user.uid;
    let cancelled = false;
    void (async () => {
      try {
        const idToken = await getFirebaseIdToken();
        const res = await fetch("/api/stripe/sync-subscription", {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          synced?: boolean;
          reason?: string;
          active?: boolean;
          status?: string;
          linkedByEmail?: boolean;
          emailLookupTried?: boolean;
        };
        if (!res.ok) {
          console.warn(
            "[AuthProvider] sync-subscription failed:",
            res.status,
            body?.error ?? body,
          );
          logSubscriptionDebug({
            phase: "stripe_reconcile_http_error",
            uid,
            email: user.email ?? null,
            httpStatus: res.status,
            error: body?.error ?? null,
          });
          return;
        }
        logSubscriptionDebug({
          phase: "stripe_reconcile",
          uid,
          email: user.email ?? null,
          source: "stripe_api_writeback",
          synced: body.synced ?? null,
          stripeStatus: body.status ?? null,
          stripeActive: body.active ?? null,
          reason: body.reason ?? null,
          linkedByEmail: body.linkedByEmail ?? null,
          emailLookupTried: body.emailLookupTried ?? null,
        });
        // Only skip future sync attempts for this uid after a successful write;
        // failed syncs (e.g. missing Firestore link) should retry on next page load.
        if (!cancelled && user.uid === uid && body.synced === true) {
          stripeSubscriptionSyncUidRef.current = uid;
        }
      } catch (e) {
        console.error("[AuthProvider] sync-subscription request failed:", e);
        logSubscriptionDebug({
          phase: "stripe_reconcile_fetch_error",
          uid,
          email: user.email ?? null,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, masqueradeUser, getFirebaseIdToken]);

  const setUiMode = async (mode: UiMode) => {
      if (!user) {
          throw new Error("User not authenticated");
      }
      setUiModeState(mode);
      await setSetting(user.uid, 'debug', { uiMode: mode });
  }

  useEffect(() => {
    const handleEmailSignIn = async () => {
      // Guard: only run in browser
      if (typeof window === 'undefined') return;
      
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        if (email) {
            try {
                const result = await signInWithEmailLink(auth, email, window.location.href);
                if (typeof window !== 'undefined') {
                  window.localStorage.removeItem('emailForSignIn');
                }
                 if (result.user) {
                    const userDocRef = v3UserDoc(result.user.uid);
                    const userDoc = await getDoc(userDocRef);
                    const existingData = userDoc.exists() ? userDoc.data() : {};
                    
                    await setDoc(userDocRef, {
                        uid: result.user.uid,
                        displayName: result.user.displayName,
                        email: result.user.email ?? '',
                        photoURL: result.user.photoURL,
                        lastLoginAt: serverTimestamp(),
                        loginCount: increment(1),
                        ...(existingData.createdAt ? {} : { createdAt: serverTimestamp() }),
                        ...(existingData.cohort ? {} : { cohort: 'rookie' }),
                        ...(existingData.onboarding ? {} : { onboarding: DEFAULT_V3_ONBOARDING }),
                        signedIn: true,
                    }, { merge: true });
                }
                router.replace(DASHBOARD_PATH);
            } catch (err: any) {
                setError(err.message);
                console.error("Error signing in with email link:", err);
                throw err;
            }
        } else {
            const errMsg = "Email is required to sign in.";
            setError(errMsg);
            console.error(errMsg);
            throw new Error(errMsg);
        }
      }
    }
    handleEmailSignIn();
  }, [router]);

  // Stable auth listener — uses refs for pathname, router, and trueAdminUser
  // so the effect runs once and never tears down / resubscribes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (!trueAdminUserRef.current) {
          trueAdminUserRef.current = user;
          setTrueAdminUser(user);
        }
        
        // Restore masquerade state from session storage
        const masqueradeInfo = sessionStorage.getItem('masqueradeUser');
        if (masqueradeInfo) {
          const targetUser = JSON.parse(masqueradeInfo);
          const masqueradedUserObject = {
              ...user,
              uid: targetUser.uid,
              displayName: targetUser.displayName,
              email: targetUser.email,
              photoURL: targetUser.photoURL,
          };
          setUser(masqueradedUserObject as User);
          setMasqueradeUser(targetUser);
        } else {
            setUser(user);
        }

        // Save/update user profile in Firestore with login tracking
        try {
          const userDocRef = v3UserDoc(user.uid);
          const userDoc = await getDoc(userDocRef);
          const existingData = userDoc.exists() ? userDoc.data() : {};
          
          const shouldIncrementLogin = (() => {
            if (!existingData.lastLoginAt) return true;
            
            const lastLogin = existingData.lastLoginAt;
            const lastLoginTime = lastLogin?.toMillis ? lastLogin.toMillis() : 
                                 typeof lastLogin === 'string' ? new Date(lastLogin).getTime() : 0;
            
            if (lastLoginTime === 0) return true;
            
            const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
            return lastLoginTime < fiveMinutesAgo;
          })();
          
          const updateData: Record<string, unknown> = {
              uid: user.uid,
              displayName: user.displayName,
              email: user.email ?? '',
              photoURL: user.photoURL,
              lastLoginAt: serverTimestamp(),
              ...(existingData.createdAt ? {} : { createdAt: serverTimestamp() }),
              ...(existingData.cohort ? {} : { cohort: 'rookie' }),
              ...(existingData.onboarding ? {} : { onboarding: DEFAULT_V3_ONBOARDING }),
              ...(existingData.loginCount != null ? {} : { loginCount: 0 }),
              signedIn: true,
          };
          
          if (shouldIncrementLogin) {
              updateData.loginCount = increment(1);
          }
          
          await setDoc(userDocRef, updateData, { merge: true });
        } catch (profileErr) {
          console.error("[AuthProvider] Failed to save user profile:", profileErr);
        }
        
        try {
            const result = await getRedirectResult(auth);
            if (result) {
                console.log("[AuthProvider] Got redirect result.");
                // Calendar-scoped redirects store token via the pending flag
                const calendarRedirectPending = sessionStorage.getItem('calendarRedirectPending');
                if (calendarRedirectPending) {
                    sessionStorage.removeItem('calendarRedirectPending');
                    const credential = GoogleAuthProvider.credentialFromResult(result);
                    const token = credential?.accessToken;
                    const refreshToken = result.user?.refreshToken;
                    if (token) {
                        sessionStorage.setItem('google-access-token', token);
                        await setDoc(doc(getV3Db(), 'users', result.user.uid, 'secrets', `google_token`), { accessToken: token, refreshToken: refreshToken }, { merge: true });
                        console.log("[AuthProvider] Stored calendar tokens from redirect.");
                    }
                }

                const returnPath = sessionStorage.getItem('authReturnPath');
                sessionStorage.removeItem('authReturnPath');
                routerRef.current.replace(returnPath && returnPath !== '/' && returnPath !== '/login' ? returnPath : DASHBOARD_PATH);
            }
        } catch(e: any) {
            console.error("[AuthProvider] Error getting redirect result", e);
            setError(e.message);
            throw e;
        }
        
      } else {
        setUser(null);
        setTrueAdminUser(null);
        trueAdminUserRef.current = null;
        setMasqueradeUser(null);
        sessionStorage.removeItem('masqueradeUser');
      }
      setLoading(false);
      
      const currentPathname = pathnameRef.current;
      const isLoginPage = currentPathname === '/login';
      
      if (user && isLoginPage) {
        routerRef.current.push(DASHBOARD_PATH);
      }
      const allowLoggedOut =
        currentPathname === '/' ||
        isLoginPage ||
        currentPathname === '/welcome' ||
        currentPathname === '/subscribe' ||
        currentPathname === '/terms' ||
        currentPathname === '/privacy' ||
        (currentPathname != null && currentPathname.startsWith('/beta-launch'));
      if (!user && !allowLoggedOut) {
        routerRef.current.replace('/');
      }
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable: uses refs for pathname/router/trueAdminUser
  }, []);

  const loginWithGoogle = async () => {
    setLoading(true);
    setError(null);
    
    sessionStorage.setItem('authReturnPath', pathname);

    try {
      await signInWithPopup(auth, googleProvider);

      const returnPath = sessionStorage.getItem('authReturnPath');
      sessionStorage.removeItem('authReturnPath');
      router.push(returnPath && returnPath !== '/' && returnPath !== '/login' ? returnPath : DASHBOARD_PATH);

    } catch (err: any) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        console.warn(`[AuthProvider] Popup error (${err.code}), falling back to redirect method.`);
        await signInWithRedirect(auth, googleProvider);
      } else {
        sessionStorage.removeItem('authReturnPath'); // Clean up if login fails
        setError(err.message);
        console.error("[AuthProvider] Error during Google login:", err);
        throw err;
      }
    } finally {
      if (error?.includes('auth/popup-blocked') || error?.includes('auth/popup-closed-by-user')) return;
      setLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.getIdToken(true);
        const tokenFromStorage = sessionStorage.getItem('google-access-token');
        if (tokenFromStorage) {
          return tokenFromStorage;
        }
        // Fallback: try Firestore
        const tokenDoc = await getDoc(
          doc(getV3Db(), 'users', auth.currentUser.uid, 'secrets', 'google_token'),
        );
        if (tokenDoc.exists()) {
          const firestoreToken = tokenDoc.data()?.accessToken;
          if (firestoreToken) {
            sessionStorage.setItem('google-access-token', firestoreToken);
            return firestoreToken;
          }
        }
        throw new Error("Google access token not found. Please re-authenticate.");
      } catch (error) {
        console.error("Error retrieving access token:", error);
        throw error;
      }
    }
    throw new Error("No authenticated user. Please sign in.");
  };

  const requestCalendarAccess = async (): Promise<string | null> => {
    try {
      const result = await signInWithPopup(auth, calendarGoogleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential?.accessToken;
      const refreshToken = result.user?.refreshToken;
      if (token) {
        sessionStorage.setItem('google-access-token', token);
        await setDoc(
          doc(getV3Db(), 'users', result.user.uid, 'secrets', 'google_token'),
          { accessToken: token, refreshToken },
          { merge: true },
        );
      }
      return token ?? null;
    } catch (err: any) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        console.warn(`[AuthProvider] Calendar popup error (${err.code}), falling back to redirect.`);
        sessionStorage.setItem('calendarRedirectPending', 'true');
        await signInWithRedirect(auth, calendarGoogleProvider);
        return null;
      }
      throw err;
    }
  };

  const loginWithEmail = async (email: string) => {
    setLoading(true);
    setError(null);
    const actionCodeSettings = {
        url: `${window.location.origin}/`,
        handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('emailForSignIn', email);
      }
    } catch (err: any) {
      setError(err.message);
      console.error("[AuthProvider] Error sending email link:", err);
      throw err;
    } finally {
        setLoading(false);
    }
  }

  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
      sessionStorage.removeItem('google-access-token');
      sessionStorage.removeItem('authReturnPath');
      sessionStorage.removeItem('masqueradeUser');
      router.replace('/');
    } catch (err: any) {
       setError(err.message);
       console.error("[AuthProvider] Error during logout:", err);
       throw err;
    } finally {
        setLoading(false);
    }
  };
  
  const masqueradeAs = (targetUser: AppUser) => {
    if (trueAdminUser) {
        sessionStorage.setItem('masqueradeUser', JSON.stringify(targetUser));
        const masqueradedUserObject = {
            ...trueAdminUser, // Base properties from the admin user
            uid: targetUser.uid,
            displayName: targetUser.displayName,
            email: targetUser.email,
            photoURL: targetUser.photoURL,
        };
        setUser(masqueradedUserObject as User);
        setMasqueradeUser(targetUser);
    }
  };

  const stopMasquerading = () => {
    sessionStorage.removeItem('masqueradeUser');
    setMasqueradeUser(null);
    if (trueAdminUser) {
      setUser(trueAdminUser);
    }
  };

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginWithEmail,
    logout,
    error,
    getAccessToken,
    getFirebaseIdToken,
    requestCalendarAccess,
    masqueradeAs,
    stopMasquerading,
    isMasquerading: !!masqueradeUser,
    trueAdminUser,
    masqueradeUser,
    isSuperSuperAdmin,
    isAdmin,
    adminList,
    fetchAdminList,
    uiMode,
    setUiMode,
    hasActiveSubscription,
    subscriptionLoading,
    enforceMasqueradeSubscription,
    setEnforceMasqueradeSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
